import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Layout } from "@/components/Layout";
import { ENTERPRISE_SERVICES, WHY_EDROY } from "@/data/content";
import { ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1741037306938-8d5bf2652415?auto=format&fit=crop&w=1600&q=80";

export default function Home() {
  return (
    <Layout>
      {/* HERO */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden" data-testid="home-hero">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="eyebrow" data-testid="home-eyebrow">
                  EdRoy Consigliere
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] mt-6 text-charcoal"
                  data-testid="home-heading"
                >
                  Strategic
                  <br />
                  <span className="italic text-gold">Workforce, Talent</span>
                  <br />
                  & HR Transformation Services.
                </h1>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-10 text-lg text-slate2 leading-relaxed max-w-xl">
                  A consulting-led HR and workforce solutions practice — solving complex talent, workforce and
                  organizational challenges for enterprises, GCCs, startups and growth-stage organizations.
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <div className="mt-12 flex flex-wrap items-center gap-5">
                  <Link to="/services" className="btn-primary" data-testid="home-cta-services">
                    Explore the services
                    <ArrowRight size={14} />
                  </Link>
                  <Link to="/career-advisory" className="btn-outline" data-testid="home-cta-career">
                    Career advisory (individuals)
                  </Link>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="relative aspect-[4/5] overflow-hidden border border-[#E0DCD3] bg-ivory-surface">
                  <img src={HERO_IMG} alt="Executive office building" className="w-full h-full object-cover" loading="eager" />
                  <div className="absolute bottom-4 left-4 right-4 bg-ivory/85 backdrop-blur-md border border-[#E0DCD3] p-5">
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <div className="tick-number text-gold text-2xl md:text-3xl">20+</div>
                        <div className="font-mono text-[8px] uppercase tracking-[0.2em] mt-1 text-slate2">Years</div>
                      </div>
                      <div className="border-l border-[#E0DCD3]">
                        <div className="tick-number text-2xl md:text-3xl">E2E</div>
                        <div className="font-mono text-[8px] uppercase tracking-[0.2em] mt-1 text-slate2">Execution</div>
                      </div>
                      <div className="border-l border-[#E0DCD3]">
                        <div className="tick-number text-2xl md:text-3xl">HR+</div>
                        <div className="font-mono text-[8px] uppercase tracking-[0.2em] mt-1 text-slate2">Tech</div>
                      </div>
                      <div className="border-l border-[#E0DCD3]">
                        <div className="tick-number text-2xl md:text-3xl">∞</div>
                        <div className="font-mono text-[8px] uppercase tracking-[0.2em] mt-1 text-slate2">Global</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT STRIP */}
      <section className="bg-charcoal text-ivory relative overflow-hidden grain grain-dark" data-testid="home-about">
        <div className="container-editorial py-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow eyebrow-slate text-gold">About the Practice</div>
              <p className="font-display text-3xl md:text-4xl mt-4 leading-tight tracking-tight text-ivory">
                A consulting-led practice — <span className="italic text-gold">advisory + execution</span>, HR + technology.
              </p>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-[#E5E1DA] leading-relaxed text-lg">
                Over two decades of experience across Talent Acquisition, Talent Management, HR Consulting, Workforce
                Strategy, and HR Technology — combining strategic advisory with strong execution capability.
              </p>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[#E5E1DA]">
                {[
                  "Solving difficult hiring challenges",
                  "Delivering execution-driven consulting",
                  "Supporting HR transformation initiatives",
                  "Bridging HR and technology effectively",
                  "Creating measurable business impact",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="text-gold">·</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES PILLARS */}
      <section className="py-24 lg:py-32" data-testid="home-services">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-5">
              <div className="eyebrow">Service Portfolio</div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-6">
                Five deliberate pillars.
                <br />
                <span className="italic text-gold">One accountable practice.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 lg:pt-6">
              <p className="text-lg text-slate2 leading-relaxed">
                Enterprise mandates delivered as a single practice — from strategic recruitment to AI-led interviewing.
                Speak with Roy to open a private conversation.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ENTERPRISE_SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.06}>
                <Link
                  to={`/services/${s.slug}`}
                  className="group block border border-[#E0DCD3] bg-white hover-swap hover:bg-charcoal hover:text-ivory h-full"
                  data-testid={`home-service-card-${s.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-ivory-surface">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="p-6 lg:p-8">
                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">{s.kicker}</div>
                    <div className="font-display text-2xl md:text-[26px] leading-tight mt-4">{s.title}</div>
                    <p className="text-sm mt-3 leading-relaxed opacity-80">{s.tagline}</p>
                    <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.24em] inline-flex items-center gap-2 border-b pb-1 border-current w-max">
                      Read the mandate
                      <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY EDROY */}
      <section className="py-24 lg:py-32 bg-ivory-surface relative overflow-hidden grain" data-testid="home-why">
        <div className="container-editorial relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-6">
              <div className="eyebrow">Why EdRoy Consigliere</div>
              <h2 className="font-display text-4xl md:text-5xl mt-6 leading-tight tracking-tight">
                Six reasons the practice is built the <span className="italic text-gold">way it is</span>.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY_EDROY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.05}>
                <div className="border border-[#E0DCD3] bg-white p-8 lg:p-10 h-full" data-testid={`home-why-${i}`}>
                  <div className="eyebrow">{String(i + 1).padStart(2, "0")}</div>
                  <div className="font-display text-2xl mt-3">{w.title}</div>
                  <p className="mt-3 text-slate2 leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CAREER ADVISORY TEASER — INDIVIDUALS */}
      <section className="py-24 lg:py-32" data-testid="home-career-teaser">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-6">
              <div className="eyebrow">For Individuals</div>
              <h2 className="font-display text-4xl md:text-5xl mt-6 leading-tight tracking-tight">
                Career Advisory & Interview <span className="italic text-gold">Preparation</span> Services.
              </h2>
              <p className="mt-8 text-lg text-slate2 leading-relaxed max-w-xl">
                A four-stage career transformation framework for professionals authoring the next chapter — Discovery,
                Preparation, Positioning and Evaluation.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/career-advisory" className="btn-primary" data-testid="home-career-cta">
                  See the framework
                </Link>
                <Link to="/book" className="btn-ghost" data-testid="home-career-book">
                  Request a discovery call →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-charcoal text-ivory relative overflow-hidden" data-testid="home-cta-section">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-end gap-10">
            <div className="lg:col-span-8">
              <div className="eyebrow eyebrow-slate text-gold">Let’s connect</div>
              <h2 className="font-display text-4xl md:text-6xl mt-6 leading-[1.05] tracking-tight text-ivory">
                Let’s connect to explore how we can support your <span className="italic text-gold">talent and HR transformation</span> goals.
              </h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/contact" className="btn-primary btn-on-dark" data-testid="home-final-contact">
                Open a conversation
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
